/**
 * ViewModeSelector — v3 frosted segmented control
 *
 * 5 icon-only modes: List, Cards, Gallery, Excel, Map.
 * Default selected: Excel (per spec §1.2).
 *
 * Spec: docs/superpowers/specs/2026-03-17-layout-information-density-design.md §1.2
 */

import { useState } from 'react';
import { List, LayoutGrid, Image, Table2, Map } from 'lucide-react';

export type ViewMode = 'list' | 'cards' | 'gallery' | 'excel' | 'map';

interface ViewModeSelectorProps {
  value?: ViewMode;
  onChange?: (mode: ViewMode) => void;
  collapsed?: boolean;
}

const VIEW_MODES: { key: ViewMode; icon: typeof List; label: string }[] = [
  { key: 'list', icon: List, label: 'List' },
  { key: 'cards', icon: LayoutGrid, label: 'Cards' },
  { key: 'gallery', icon: Image, label: 'Gallery' },
  { key: 'excel', icon: Table2, label: 'Excel' },
  { key: 'map', icon: Map, label: 'Map' },
];

export function ViewModeSelector({ value, onChange, collapsed = false }: ViewModeSelectorProps) {
  const [internalMode, setInternalMode] = useState<ViewMode>('excel');
  const activeMode = value ?? internalMode;

  const handleChange = (mode: ViewMode) => {
    setInternalMode(mode);
    onChange?.(mode);
  };

  if (collapsed) {
    // Collapsed: show only active mode icon in 28px rounded square
    const active = VIEW_MODES.find(m => m.key === activeMode)!;
    const Icon = active.icon;
    return (
      <button
        type="button"
        data-testid="view-mode-collapsed"
        title={`View: ${active.label}`}
        className="flex items-center justify-center w-7 h-7 rounded-md transition-colors
          bg-[rgba(255,255,255,0.5)] dark:bg-[rgba(2,6,23,0.4)]"
        onClick={() => {
          const idx = VIEW_MODES.findIndex(m => m.key === activeMode);
          handleChange(VIEW_MODES[(idx + 1) % VIEW_MODES.length].key);
        }}
      >
        <Icon size={16} strokeWidth={1.5} className="text-t1" />
      </button>
    );
  }

  return (
    <div data-testid="view-mode-selector">
      {/* Section label — spec: 9px uppercase, secondary text, etched */}
      <div
        className="text-whisper-xs uppercase font-semibold tracking-[0.5px] text-etched mb-2
          text-t2"
      >
        View
      </div>

      {/* Frosted segmented control — spec §1.2 */}
      <div
        className="flex items-center gap-0.5 p-[3px] rounded-glass
          bg-[rgba(0,0,0,0.06)] dark:bg-[rgba(255,255,255,0.05)]"
      >
        {VIEW_MODES.map((mode) => {
          const Icon = mode.icon;
          const isActive = activeMode === mode.key;

          return (
            <button
              key={mode.key}
              type="button"
              data-testid={`view-mode-${mode.key}`}
              title={mode.label}
              aria-pressed={isActive}
              onClick={() => handleChange(mode.key)}
              className={[
                'flex items-center justify-center flex-1 h-7 rounded-[5px] transition-all duration-150',
                isActive
                  ? 'bg-[rgba(255,255,255,0.8)] dark:bg-[rgba(2,6,23,0.6)] shadow-sm'
                  : 'bg-transparent',
              ].join(' ')}
            >
              <Icon
                size={16}
                strokeWidth={1.5}
                className={
                  isActive
                    ? 'text-[#3b3a37] dark:text-[#9a9890]'
                    : 'text-[#6b6a65]/50 dark:text-[#9a9890]/50'
                }
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
